/**
 * AudioAlign — find the time offset between two related audio signals.
 *
 * The problem (aksinia, 2026-07-17): a vocal recorded separately from an
 * instrumental never shares its start point, so a client mix that starts both
 * at zero lands the voice off the music. The DAW answer is a manual nudge (we
 * ship that slider); the ETALON answer is to find the offset automatically and
 * leave the slider for fine trim. When the vocal was recorded in the same room
 * as the playing instrumental (the single most common home setup — phone in
 * hand, music on speakers), the recording CONTAINS bleed of the instrumental,
 * and envelope cross-correlation recovers the offset almost exactly. When the
 * vocal is clean a-cappella there is still shared rhythm to correlate, but far
 * weaker — which is why the result carries a CONFIDENCE and the caller must
 * only auto-apply above a threshold (validated on real files, see the
 * align-selftest harness in tests/): a wrong auto-shift is worse than none.
 *
 * Pure DSP — no DOM, no WebAudio. Runs identically in the browser (window
 * .AudioAlign) and in Node (module.exports), so the algorithm is testable on
 * real WAV files without a browser in the loop.
 *
 * API:
 *   AudioAlign.findOffset(pcmA, srA, pcmB, srB, opts?) -> {
 *     offsetSec,    // how much A lags B: >0 = A starts later than B
 *     confidence,   // peak-to-sidelobe ratio; >=2.0 is a confident lock
 *   }
 *   Convention used by voice-cover: A = vocal, B = instrumental;
 *   offsetSec > 0 means the voice enters later — start the MUSIC that much
 *   earlier (or the voice later) to line them up.
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.AudioAlign = factory();
})(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    var ENV_HZ = 100;        // feature rate: 10 ms resolution
    var MAX_LAG_S = 10;      // search window in either direction
    var FFT_N = 512;         // 32 ms frames @16 kHz
    // Lock threshold — every number below is MEASURED on real prod audio
    // (2026-07-17 harness: pop-utro vocal/instrumental + ballad-vecher):
    //   true pair, mix bleed        ncc 0.136
    //   true pair, clean a-cappella ncc 0.139
    //   WRONG SONG (real music)     ncc 0.111  <- the case a 0.09 cut missed
    //   pink noise                  ncc 0.051
    // 0.125 sits between the wrong-song and true-pair clusters. The margin is
    // honest but thin; mix_align_no_lock/mix_auto_aligned telemetry carries the
    // ncc so the field distribution can recalibrate this. Fail-safe by design:
    // below threshold nothing moves and the manual slider is exactly as before.
    var LOCK_NCC = 0.125;
    // Second, INDEPENDENT lock criterion: statistical significance of the peak.
    // NCC between two unrelated signals over an overlap of N frames has a noise
    // floor ~1/sqrt(N); the max over ~2000 candidate lags reaches ~4/sqrt(N).
    // With a short vocal (6-12 s -> 600-1200 frames) that floor is 0.13-0.16 —
    // ABOVE LOCK_NCC, so a threshold on ncc alone false-locks pure chance (two
    // independent noise beds measured ncc 0.127 / z 3.1 in the synthetic guard).
    // z = ncc * sqrt(overlapFrames) normalises that out: unrelated signals stay
    // z <~ 4.2 no matter the length, true pairs measured z 7+ (0.136 * sqrt of
    // a ~50 s overlap). Both gates must pass; either alone has a blind spot
    // (ncc-only: short-overlap flukes; z-only: wrong SONG correlates ~0.111
    // genuinely, and a long overlap would push its z past any fixed cut).
    var LOCK_Z = 4.5;

    /**
     * The feature is SPECTRAL FLUX (onset novelty), not loudness. The first cut
     * used an RMS envelope and its own real-file harness killed it: a modern
     * mastered instrumental is compressed nearly FLAT, so its mean-removed
     * envelope is ~zero-information (NCC at the known-true lag measured 0.026).
     * What survives both the mix bleed and a singer on top is WHEN NEW SOUND
     * ARRIVES — drum hits, chord changes. Flux = positive spectral change per
     * frame; the same events spike in both signals, and windowed NCC locks.
     */
    function iterFFT(re, im) {
        var n = re.length;
        for (var i = 1, j = 0; i < n; i++) {           // bit-reversal permutation
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j |= bit;
            if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var ang = -2 * Math.PI / len, wr = Math.cos(ang), wi = Math.sin(ang);
            for (i = 0; i < n; i += len) {
                var cr = 1, ci = 0;
                for (j = 0; j < len / 2; j++) {
                    var ur = re[i + j], ui = im[i + j];
                    var vr = re[i + j + len / 2] * cr - im[i + j + len / 2] * ci;
                    var vi = re[i + j + len / 2] * ci + im[i + j + len / 2] * cr;
                    re[i + j] = ur + vr; im[i + j] = ui + vi;
                    re[i + j + len / 2] = ur - vr; im[i + j + len / 2] = ui - vi;
                    var ncr = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = ncr;
                }
            }
        }
    }

    function envelope(pcm, sr) {                        // spectral-flux novelty @ ENV_HZ
        var hop = Math.max(1, Math.round(sr / ENV_HZ));
        var frames = Math.max(0, Math.floor((pcm.length - FFT_N) / hop));
        var half = FFT_N / 2;
        var hann = new Float64Array(FFT_N);
        for (var i = 0; i < FFT_N; i++) hann[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (FFT_N - 1));
        var prev = new Float64Array(half);
        var env = new Float64Array(frames);
        var re = new Float64Array(FFT_N), im = new Float64Array(FFT_N);
        for (var f = 0; f < frames; f++) {
            var base = f * hop;
            for (i = 0; i < FFT_N; i++) { re[i] = (pcm[base + i] || 0) * hann[i]; im[i] = 0; }
            iterFFT(re, im);
            var flux = 0;
            for (i = 1; i < half; i++) {
                var mag = Math.sqrt(re[i] * re[i] + im[i] * im[i]);
                var d = mag - prev[i];
                if (d > 0) flux += d;
                prev[i] = mag;
            }
            env[f] = flux;
        }
        // The first frames diff against a zero `prev` spectrum — a fake novelty
        // explosion at t=0 of EVERY file. DROP them, don't zero them: zeroed
        // warm-ups became identical mean-removed "steps" at the head of BOTH
        // signals and false-locked two independent noise beds at lag 0 (ncc 0.45,
        // caught by the synthetic guard). A one-frame trim shifts both features
        // identically, so the measured offset is unaffected.
        var trimmed = env.subarray(Math.min(8, frames));
        // Mean-remove per usual so quiet stretches don't self-correlate.
        var mean = 0;
        for (i = 0; i < trimmed.length; i++) mean += trimmed[i];
        mean /= (trimmed.length || 1);
        for (i = 0; i < trimmed.length; i++) trimmed[i] -= mean;
        return trimmed;
    }

    /** Prefix sums of squares, for O(1) windowed energies. */
    function prefixSq(a) {
        var p = new Float64Array(a.length + 1);
        for (var i = 0; i < a.length; i++) p[i + 1] = p[i] + a[i] * a[i];
        return p;
    }

    /**
     * WINDOWED normalised cross-correlation over ±maxLag.
     *
     * The first cut of this function normalised by the GLOBAL energies and failed
     * its own real-file harness spectacularly (peaks pinned at the window edge,
     * offsets ±9 s on a known 1.37 s case): mean-removed envelopes are negative
     * through every pause, and at extreme lags the short overlap pairs the
     * vocal's silences with the outro's silences — negative × negative sums into
     * a huge fake peak that global norms don't punish. True NCC normalises by the
     * energies INSIDE the overlap, and a minimum-overlap guard refuses lags where
     * only a sliver of the signals would be compared.
     */
    function findOffset(pcmA, srA, pcmB, srB, opts) {
        opts = opts || {};
        var maxLag = Math.round((opts.maxLagS || MAX_LAG_S) * ENV_HZ);
        var minOverlap = Math.round((opts.minOverlapS || 5) * ENV_HZ);
        var a = envelope(pcmA, srA);
        var b = envelope(pcmB, srB);
        var pa = prefixSq(a), pb = prefixSq(b);

        var lags = [], nccs = [], ovls = [];
        for (var lag = -maxLag; lag <= maxLag; lag++) {
            var start = Math.max(0, -lag);
            var end = Math.min(a.length, b.length - lag);
            if (end - start < minOverlap) continue;
            var num = 0;
            for (var i = start; i < end; i++) num += a[i] * b[i + lag];
            var ea = pa[end] - pa[start];
            var eb = pb[end + lag] - pb[start + lag];
            var den = Math.sqrt(ea * eb);
            if (den <= 0) continue;
            lags.push(lag); nccs.push(num / den); ovls.push(end - start);
        }
        if (!nccs.length) return { offsetSec: 0, confidence: 0, zscore: 0, locked: false };

        var bi = 0;
        for (i = 1; i < nccs.length; i++) if (nccs[i] > nccs[bi]) bi = i;

        // Confidence = the RAW NCC of the peak — the third candidate measure, and
        // the one the real-file harness finally validated. best/second ratio:
        // useless (music self-similarity, 1.19 true vs 1.22 noise). Peak-vs-own-
        // profile-std z: useless (3.45 true vs 3.37 noise — noise peaks over its
        // own flat floor too; NOT the overlap-normalised z used in `locked`).
        // Raw peak NCC separates with 2.7× margin: 0.136-0.139 on every true
        // pair (bleed AND clean a-cappella) vs 0.051 on pink noise. LOCK_NCC sits
        // between with room both ways. Callers must not auto-apply below it.
        //
        // Accuracy note, measured: the auto offset lands ~70-80 ms off the sample
        // truth, and that is the FEATURE's ceiling, not a bug — singers phrase
        // BEHIND the beat, so vocal novelty correlates to the music ~70 ms late
        // (the true bleed lag was literally the next peak down). Coarse lock by
        // AudioAlign + fine trim by the human slider (25 ms steps) is the design.

        // Parabolic sub-sample interpolation of the peak (its two neighbours in
        // lag space) shaves the discretisation part of the residual error.
        var frac = 0;
        if (bi > 0 && bi < nccs.length - 1
            && lags[bi] - lags[bi - 1] === 1 && lags[bi + 1] - lags[bi] === 1) {
            var y0 = nccs[bi - 1], y1 = nccs[bi], y2 = nccs[bi + 1];
            var denom = (y0 - 2 * y1 + y2);
            if (denom !== 0) frac = 0.5 * (y0 - y2) / denom;
            if (!(frac > -1 && frac < 1)) frac = 0;
        }

        var z = nccs[bi] * Math.sqrt(ovls[bi]);
        var out = {
            // positive = A (the vocal) lags B (the music)
            offsetSec: -(lags[bi] + frac) / ENV_HZ,
            confidence: nccs[bi],
            zscore: z,
            locked: nccs[bi] >= LOCK_NCC && z >= LOCK_Z,
        };
        if (opts.debug) {
            var idx = lags.map(function (_, k) { return k; }).sort(function (x, y) { return nccs[y] - nccs[x]; });
            out.top = idx.slice(0, 6).map(function (k) { return { lagS: lags[k] / ENV_HZ, ncc: +nccs[k].toFixed(4) }; });
            out.at = function (lagS) { var k = lags.indexOf(Math.round(lagS * ENV_HZ)); return k >= 0 ? +nccs[k].toFixed(4) : null; };
        }
        return out;
    }

    /**
     * The offset for a take that was sung WHILE the backing track played — the
     * case where the answer is arithmetic, not a correlation guess.
     *
     * SIGN CONVENTION (the same one findOffset's callers use): a POSITIVE result
     * delays the VOICE, a NEGATIVE one delays the MUSIC. The music becomes
     * audible `leadMs` after the take starts, plus the platform's output latency
     * — so the voice in that take is BEHIND the music by exactly that much, and
     * the compensation is NEGATIVE. Getting this backwards does not misalign a
     * little: it doubles the error, which is why it lives here, next to the
     * convention it must match, with a test that pins the sign.
     *
     * @param {{leadMs:number, outputLatencyMs:number}} o
     * @returns {number} ms to feed the mix slider
     */
    function singAlongOffsetMs(o) {
        var lead = Math.max(0, (o && o.leadMs) || 0);
        var lat = Math.max(0, (o && o.outputLatencyMs) || 0);
        return -Math.round(lead + lat);
    }

    return { findOffset: findOffset, singAlongOffsetMs: singAlongOffsetMs, _envelope: envelope };
});
